import { useState, useCallback } from 'react';
import useApi from './useApi';
import { useAppSettings } from '../hooks/useAppSettings';
import { ProductListItem } from '../types/ProductTypes';
import { mapQueryProductMetaDataResponse } from '../factory/ProductFactory';

export interface ProductQueryParams {
    search?: string;
    includeInactive?: boolean;
    onlyApproved?: boolean;
    categoryIds?: number[];
    chartererIds?: number[];
    draegerTubeId?: number;
    procedureId?: number;
    page?: number;
    pageSize?: number;
    sortAscending?: boolean;
    // Search scope flags
    searchTradeNames?: boolean;
    searchCategoryNames?: boolean;
    searchSynonyms?: boolean;

    searchCasNumber?: boolean;
    // MARPOL regulation filter
    marpolRegulationCodes?: number[];
}

export interface ProductQueryResult {
    items: ProductListItem[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
}

/**
 * Custom hook for querying products using QueryProductMetaData endpoint
 * Supports pagination and server-side filtering for better performance
 */
const useProductQuery = () => {
    const { getApiData } = useApi();
    const { settings: appSettings } = useAppSettings();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<ProductQueryResult | null>(null);

    /**
     * Query products with the specified parameters
     */
    const queryProducts = useCallback(
        async (params: ProductQueryParams = {}): Promise<ProductQueryResult> => {
            const apiUrl = appSettings?.api;
            if (!apiUrl) {
                throw new Error('API URL not configured in app settings');
            }

            setIsLoading(true);
            setError(null);

            try {
                // Build query parameters
                const queryParams = new URLSearchParams();

                if (params.search) {
                    queryParams.append('search', params.search);
                }
                if (params.includeInactive !== undefined) {
                    queryParams.append('includeInactive', String(params.includeInactive));
                }
                if (params.onlyApproved !== undefined) {
                    queryParams.append('onlyApproved', String(params.onlyApproved));
                }
                if (params.categoryIds && params.categoryIds.length > 0) {
                    queryParams.append('categoryIds', params.categoryIds.join(','));
                }
                if (params.chartererIds && params.chartererIds.length > 0) {
                    queryParams.append('chartererIds', params.chartererIds.join(','));
                }
                if (params.draegerTubeId !== undefined) {
                    queryParams.append('draegerTubeId', String(params.draegerTubeId));
                }
                if (params.procedureId !== undefined) {
                    queryParams.append('procedureId', String(params.procedureId));
                }
                if (params.page !== undefined) {
                    queryParams.append('page', String(params.page));
                }
                if (params.pageSize !== undefined) {
                    queryParams.append('pageSize', String(params.pageSize));
                }
                if (params.sortAscending !== undefined) {
                    queryParams.append('sortAscending', String(params.sortAscending));
                }
                // Search scope flags — only send when false (true is the server default)
                if (params.searchTradeNames === false) {
                    queryParams.append('searchTradeNames', 'false');
                }
                if (params.searchCategoryNames === false) {
                    queryParams.append('searchCategoryNames', 'false');
                }
                if (params.searchSynonyms === false) {
                    queryParams.append('searchSynonyms', 'false');
                }
                if (params.searchCasNumber === false) {
                    queryParams.append('searchCasNumber', 'false');
                }
                // MARPOL regulation filter
                if (params.marpolRegulationCodes && params.marpolRegulationCodes.length > 0) {
                    queryParams.append('marpolRegulationCodes', params.marpolRegulationCodes.join(','));
                }

                const queryString = queryParams.toString();
                const endpoint = queryString ? `${apiUrl}QueryProductMetaData?${queryString}` : `${apiUrl}QueryProductMetaData`;

                console.log('[useProductQuery] Querying products:', endpoint);

                const response = await getApiData({ endpoint });

                // Map response to ProductListItem array
                const items = mapQueryProductMetaDataResponse(response);

                const queryResult: ProductQueryResult = {
                    items,
                    totalCount: response?.totalCount ?? items.length,
                    pageNumber: response?.pageNumber ?? params.page ?? 1,
                    pageSize: response?.pageSize ?? params.pageSize ?? 50,
                };

                console.log(`[useProductQuery] Retrieved ${items.length} products (total: ${queryResult.totalCount})`);

                setResult(queryResult);
                return queryResult;
            } catch (err: any) {
                const errorMessage = err.message || 'Failed to query products';
                console.error('[useProductQuery] Error:', errorMessage);
                setError(errorMessage);
                throw err;
            } finally {
                setIsLoading(false);
            }
        },
        [appSettings?.api, getApiData]
    );

    return {
        queryProducts,
        result,
        isLoading,
        error,
    };
};

export default useProductQuery;
